import React, { useState, useEffect } from "react";
import { ButtonPrimaryStyled, ButtonsContainer } from "../../custom/buttons/buttons.styled";
import { useForm, FormProvider } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { BackNextButton } from "src/components/custom";
import { toast } from "react-hot-toast";
import { ICareer } from "src/Interfaces/career";
import { useLocale } from "../../../i18n/localeContext";
import { getUserEnrollementPage } from "../../../Api/content";
import { Skeleton } from "antd";
import { StrapiError } from "../../../pages/StrapiError";
import { formatStrapiTemplate } from "../../../utils/strapi-template";

interface IForm {
  formCurrentItem?: JSX.Element;
  readyForSubmit: boolean;
  goBack: () => void;
  goToNext: () => void;
  stepIndex: number;
  careers: ICareer[];
  callbackIfPreviousRoute: (program: number, period: number) => void;
}
const RegisterForm = ({
  formCurrentItem,
  readyForSubmit,
  goBack,
  goToNext,
  stepIndex,
  careers,
  callbackIfPreviousRoute,
}: IForm) => {
  const navigate = useNavigate();
  const methods = useForm();
  const { locale } = useLocale();
  const [userEnrollementPage, setUserEnrollementPage] = useState<any>(null);
  const [isLoadingContent, setIsLoadingContent] = useState<boolean>(true);
  const [hasError, setHasError] = useState<boolean>(false);
  const { handleSubmit, setValue, register } = methods;

  const { program, period } = useParams();

  React.useEffect(() => {
    register("programa", {
      required: true,
    });
    register("aceptaTerminos", {
      required: true,
    });
    register("esR3", {
      required: false,
    });
    register("materiasSeleccionadas");
  }, [register]);

  React.useEffect(() => {
    if (program && program !== "initial" && period) {
      setValue("programa", {
        carreraId: +program,
        seleccionSinTerminar: careers?.find((c) => c.tieneSeleccionSinTerminar === true),
      });
      setValue("aceptaTerminos", true);
      callbackIfPreviousRoute(+program, +period);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toastErrorStyle = {
    style: {
      padding: "16px",
      color: "#e74c3c",
      fontSize: 18,
      fontWeight: 700,
    },
    iconTheme: {
      primary: "#e74c3c",
      secondary: "#FFF",
    },
  };

  const onSubmit = () => {
    if (stepIndex === 1 && methods.getValues("materiasSeleccionadas")?.length === 0)
      toast.error(
        userEnrollementPage?.error_no_subjects_selected ||
          "Debe realizar la selección de materias para continuar.",
        toastErrorStyle,
      );
    else {
      navigate("/cart/selection");
    }
  };

  const goToNextStep = () => {
    let error = null;
    if (stepIndex === 0) {
      const programa = methods.getValues("programa");
      const esR3 = methods.getValues("esR3");
      const aceptaTerminos = methods.getValues("aceptaTerminos");
      if (!programa) {
        error =
          userEnrollementPage?.error_no_program_selected ||
          "Debe seleccionar un programa para continuar.";
      } else if (
        programa.seleccionSinTerminar &&
        programa.seleccionSinTerminar.carreraId !== programa.carreraId
      ) {
        error = formatStrapiTemplate(
          userEnrollementPage?.error_pending_selection,
          `Debe completar la selección pendiente en el programa ${programa.seleccionSinTerminar.carreraNombre} antes de matricular en otro programa.`,
          { programName: programa.seleccionSinTerminar.carreraNombre },
        );
      } else if (!aceptaTerminos) {
        error =
          userEnrollementPage?.error_accept_terms || "Debe aceptar los términos para continuar.";
      } else if (esR3) {
        error =
          userEnrollementPage?.error_r3_courses ||
          "Usted presenta uno o varios cursos perdidos 3 o más veces, por lo que debe hacer su matrícula por medio de la Vicerrectoría de Docencia";
      }
    } else if (stepIndex === 1 && methods.getValues("materiasSeleccionadas")?.length === 0)
      error =
        userEnrollementPage?.error_no_subjects_selected ||
        "Debe realizar la selección de materias para continuar.";
    if (error) toast.error(error, toastErrorStyle);
    else goToNext();
  };

  const renderNextBackButtons = () => (
    <BackNextButton
      goBack={goBack}
      goToNext={goToNextStep}
      omitBack={stepIndex === 0}
      omitNext={readyForSubmit}
    />
  );

  useEffect(() => {
    let cancel = false;
    setHasError(false);
    setIsLoadingContent(true);
    const timeoutId = setTimeout(() => {
      if (!cancel) {
        setUserEnrollementPage(null);
        setIsLoadingContent(false);
        setHasError(locale === 'en');
      }
    }, 3000);
    getUserEnrollementPage(locale)
      .then((res) => {
        const data = res?.data?.attributes || res?.data || res;
        if (!cancel) {
          clearTimeout(timeoutId);
          setUserEnrollementPage(data);
          setIsLoadingContent(false);
        }
      })
      .catch(() => {
        if (!cancel) {
          clearTimeout(timeoutId);
          setUserEnrollementPage(null);
          setIsLoadingContent(false);
          setHasError(locale === 'en');
        }
      });
    return () => {
      cancel = true;
      clearTimeout(timeoutId);
    };
  }, [locale]);

  if (hasError && locale === 'en') {
    return <StrapiError
      onRetry={() => {
        setHasError(false);
        setIsLoadingContent(true);
      }}
      onUseDefault={() => {
        setHasError(false);
        setIsLoadingContent(false);
        setUserEnrollementPage(null);
      }}
    />;
  }
  return (
    <FormProvider {...methods}>
      <form>
        {formCurrentItem}
        <ButtonsContainer>
          {renderNextBackButtons()}
          {readyForSubmit && (
            <ButtonPrimaryStyled type="primary" size="large" onClick={handleSubmit(onSubmit)}>
              {isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 100 }} /> : (userEnrollementPage?.car_button || "Ir al carrito")}
            </ButtonPrimaryStyled>
          )}
        </ButtonsContainer>
      </form>
    </FormProvider>
  );
};

export default RegisterForm;
